import { useLayoutEffect, useMemo, useState } from 'react';
import { SwiperProps } from 'swiper/react';

type BrandSliderHook = {
  dynamicSlidesPerView: number;
  enableLoopAndAutoplay: boolean;
  sliderProps: SwiperProps;
};

export const useBrandSlider = (references: number[]): BrandSliderHook => {
  const [slidesPerView, setSlidesPerView] = useState(4);

  const enableLoopAndAutoplay = useMemo(
    () => references.length > slidesPerView,
    [references.length, slidesPerView]
  );

  const dynamicSlidesPerView = useMemo(
    () => (references.length >= slidesPerView ? slidesPerView : references.length),
    [slidesPerView, references.length]
  );

  useLayoutEffect(() => {
    const handleResize = () => {
      const windowWidth = Number(window.innerWidth);
      if (windowWidth >= 1200) {
        setSlidesPerView(5);
      } else if (windowWidth < 1200 && windowWidth >= 1050) {
        setSlidesPerView(4);
      } else if (windowWidth < 1050 && windowWidth >= 900) {
        setSlidesPerView(5);
      } else if (windowWidth < 900 && windowWidth >= 600) {
        setSlidesPerView(4);
      } else if (windowWidth < 600 && windowWidth >= 480) {
        setSlidesPerView(3);
      } else if (windowWidth < 480 && windowWidth >= 420) {
        setSlidesPerView(2);
      } else {
        setSlidesPerView(1.5);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const sliderProps: SwiperProps = useMemo(
    () => ({
      spaceBetween: 10,
      speed: 11000,
      slidesPerView: 'auto',
      lazy: true,
      loop: enableLoopAndAutoplay,
      allowTouchMove: enableLoopAndAutoplay,
      grabCursor: enableLoopAndAutoplay,
      freeMode: enableLoopAndAutoplay,
      autoplay: enableLoopAndAutoplay
        ? {
            delay: 0.5,
            disableOnInteraction: false,
          }
        : false,
      breakpoints: {
        0: {
          slidesPerView: 1.5,
          spaceBetween: 0,
        },
        420: {
          slidesPerView: 2,
          spaceBetween: 5,
        },
        480: {
          slidesPerView: dynamicSlidesPerView,
          spaceBetween: 5,
        },
        600: {
          slidesPerView: dynamicSlidesPerView,
          spaceBetween: 10,
        },
        900: {
          slidesPerView: dynamicSlidesPerView,
          spaceBetween: 20,
        },
        1050: {
          slidesPerView: dynamicSlidesPerView,
          spaceBetween: 10,
        },
        1200: {
          slidesPerView: dynamicSlidesPerView,
          spaceBetween: 10,
        },
      },
    }),
    [enableLoopAndAutoplay, dynamicSlidesPerView]
  );
  return {
    dynamicSlidesPerView,
    enableLoopAndAutoplay,
    sliderProps,
  };
};
